import { ReactNode, JSX } from 'react';

import WindowService, {
  SERVER_CONFIG_ELEMENT_ID,
} from '@src/services/window.service';
import { PageContextProvider } from '@src/contexts/providers/page-context-provider';
import { AnalyticsUserProvider } from '@src/contexts/providers/analytics-user-provider';
import { AnalyticsProvider } from '@src/contexts/providers/analytics-provider';
import { AbTestingProvider } from '@src/contexts/providers/ab-testing-provider';
import { LoginRedirectProvider } from '@src/contexts/providers/login-redirect-provider';
import { ServerConfigProvider } from '@src/contexts/providers/server-config-provider';
import { SpinnerContextProvider } from '@src/contexts/providers/spinner-provider';
import { ExtraData } from '@src/@types/analytics';

const AB_TESTING_INIT_ELEMENT_ID = 'hc-pet-insurance__ab-testing-init';

/**
 * Wraps the top-level component for a page with global contexts needed for
 * rendering. Should be used only once as the root element passed to `render()`
 *
 * Any data provided by the backend through the DOM should be accessed from this
 * component only to avoid polluting our React components with DOM and Window
 * access.
 */
export const GlobalContexts = ({
  children,
  pageContextData,
  analyticsExtraData,
}: {
  pageContextData?: Record<string, string>;
  children: ReactNode;
  analyticsExtraData?: ExtraData;
}): JSX.Element => {
  const { analyticsRequested, gtmParams, gtmContainerId, rumRequested } =
    getAnalyticsInitParams();
  const { abTestingRequested, optimizelySdkKey, optimizelyDataFile } =
    getAbTestingInitParams();
  const {
    headerEnv,
    awsRegionShort,
    appVersion,
    lemonadeStates,
    akamaiBotHeader,
    abTestingEnabled,
    smsConsentEnabled,
    addressCheckEnabled,
    trupanionPreferredRetailerEnabled,
    guestQuoteEnabled,
    preGuestQuoteEmailCaptureEnabled,
    isCSR,
    quoteSimplificationEnabled,
    proxyBasePath,
    breedEducationModuleEnabled,
    chewyAnalyticsAleEnabled,
    visionM2Enabled,
    lemonadeEnhancedPreventativeBenefitsStates,
  } = getServerConfigParams();

  return (
    <LoginRedirectProvider>
      <AnalyticsUserProvider>
        <ServerConfigProvider
          headerEnv={headerEnv}
          awsRegionShort={awsRegionShort}
          appVersion={appVersion}
          lemonadeStates={lemonadeStates}
          akamaiBotHeader={akamaiBotHeader}
          abTestingEnabled={abTestingEnabled}
          smsConsentEnabled={smsConsentEnabled}
          addressCheckEnabled={addressCheckEnabled}
          trupanionPreferredRetailerEnabled={trupanionPreferredRetailerEnabled}
          guestQuoteEnabled={guestQuoteEnabled}
          preGuestQuoteEmailCaptureEnabled={preGuestQuoteEmailCaptureEnabled}
          isCSR={isCSR}
          quoteSimplificationEnabled={quoteSimplificationEnabled}
          proxyBasePath={proxyBasePath}
          breedEducationModuleEnabled={breedEducationModuleEnabled}
          chewyAnalyticsAleEnabled={chewyAnalyticsAleEnabled}
          visionM2Enabled={visionM2Enabled}
          lemonadeEnhancedPreventativeBenefitsStates={
            lemonadeEnhancedPreventativeBenefitsStates
          }
        >
          <PageContextProvider pageContextData={pageContextData}>
            <AnalyticsProvider
              analyticsRequested={analyticsRequested}
              gtmParams={gtmParams}
              gtmContainerId={gtmContainerId}
              analyticsExtraData={analyticsExtraData}
            >
              <AbTestingProvider
                abTestingRequested={abTestingRequested}
                optimizelySdkKey={optimizelySdkKey}
                optimizelyDataFile={optimizelyDataFile}
              >
                <SpinnerContextProvider>{children}</SpinnerContextProvider>
              </AbTestingProvider>
            </AnalyticsProvider>
          </PageContextProvider>
        </ServerConfigProvider>
      </AnalyticsUserProvider>
    </LoginRedirectProvider>
  );
};
/** Pull analytics initialization params from the window */
const getAnalyticsInitParams = () => {
  const analyticsRequested = WindowService.window.pi.analyticsEnabled;
  const gtmParams = WindowService.window.pi.gtmParams;
  const gtmContainerId = WindowService.window.pi.gtmContainerId;
  const rumRequested = WindowService.window.pi.rumEnabled;

  if (analyticsRequested && !gtmContainerId) {
    console.error(
      'Analytics initialization params were not provided in globals. ' +
        'Analytics will not function.',
    );
  }

  return {
    analyticsRequested,
    gtmParams,
    gtmContainerId,
    rumRequested,
  };
};

/** Pull A/B testing initialization params from the DOM **/
const getAbTestingInitParams = () => {
  const abTestingRequested = WindowService.window.pi.toggles.abTestingEnabled;
  const abTestingInitElement: HTMLElement | null =
    abTestingRequested &&
    document.querySelector(`#${AB_TESTING_INIT_ELEMENT_ID}`);
  const {
    sdkKey: optimizelySdkKey,
    optimizelyDataFile: optimizelyDataFileJson,
  } = abTestingRequested && (abTestingInitElement?.dataset ?? {});
  let optimizelyDataFile: unknown = undefined;
  if (abTestingRequested && optimizelyDataFileJson) {
    try {
      optimizelyDataFile = JSON.parse(optimizelyDataFileJson);
    } catch (e) {
      console.error('Optimizely data file could not be parsed as JSON.', e);
    }
  }

  if (abTestingRequested && (!optimizelySdkKey || !optimizelyDataFile)) {
    console.error(
      'A/B testing initialization params were not provided in DOM. ' +
        'A/B tests will not function.',
    );
  }

  return {
    abTestingRequested,
    optimizelySdkKey: optimizelySdkKey ?? null,
    optimizelyDataFile: optimizelyDataFile ?? undefined,
  };
};

/** Pull Server Config params from the DOM **/
const getServerConfigParams = () => {
  const serverConfigElement: HTMLElement | null =
    WindowService.document.querySelector(`#${SERVER_CONFIG_ELEMENT_ID}`);

  if (serverConfigElement == null) {
    console.error(
      `Server config element #${SERVER_CONFIG_ELEMENT_ID} was not provided in DOM.
      Server config data will fallback to default values.`,
    );
  }

  const {
    headerEnv,
    awsRegionShort,
    appVersion,
    lemonadeStates: lemonadeStatesString,
    akamaiBotHeader,
    abTestingEnabled,
    smsConsentEnabled,
    addressCheckEnabled,
    trupanionPreferredRetailerEnabled,
    guestQuoteEnabled,
    preGuestQuoteEmailCaptureEnabled,
    isCsr: isCSR,
    quoteSimplificationEnabled,
    proxyBasePath,
    breedEducationModuleEnabled,
    chewyAnalyticsAleEnabled,
    visionM2Enabled,
    lemonadeEnhancedPreventativeBenefitsStates:
      lemonadeEnhancedPreventativeBenefitsStatesString,
  } = serverConfigElement?.dataset ?? {};

  const lemonadeStates = (lemonadeStatesString ?? '')
    .replace(/[\[\]]/g, '')
    .split(',')
    .map((s) => s.trim());

  const lemonadeEnhancedPreventativeBenefitsStates = (
    lemonadeEnhancedPreventativeBenefitsStatesString ?? ''
  )
    .replace(/[\[\]]/g, '')
    .split(',')
    .map((s) => s.trim());

  return {
    headerEnv: headerEnv ?? null,
    awsRegionShort: awsRegionShort ?? null,
    appVersion: appVersion ?? null,
    lemonadeStates: lemonadeStates,
    akamaiBotHeader: akamaiBotHeader ?? '',
    abTestingEnabled: abTestingEnabled === 'true',
    smsConsentEnabled: smsConsentEnabled === 'true',
    addressCheckEnabled: addressCheckEnabled === 'true',
    trupanionPreferredRetailerEnabled:
      trupanionPreferredRetailerEnabled === 'true',
    guestQuoteEnabled: guestQuoteEnabled === 'true',
    preGuestQuoteEmailCaptureEnabled:
      preGuestQuoteEmailCaptureEnabled === 'true',
    isCSR: isCSR === 'true',
    quoteSimplificationEnabled: quoteSimplificationEnabled === 'true',
    proxyBasePath: proxyBasePath ?? '',
    breedEducationModuleEnabled: breedEducationModuleEnabled === 'true',
    chewyAnalyticsAleEnabled: chewyAnalyticsAleEnabled === 'true',
    visionM2Enabled: visionM2Enabled === 'true',
    lemonadeEnhancedPreventativeBenefitsStates:
      lemonadeEnhancedPreventativeBenefitsStates,
  };
};
